/* eslint-disable */
// @ts-nocheck

import { CheckCircleIcon, XCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import APIServicesV2 from '@/api/APIServicesV2'
import { getContainerCategories } from '@/api/containerCategoriesAPI'
import { getCountries } from '@/api/countriesAPI'
import { getManagers } from '@/api/managersAPI'
import { getProviders } from '@/api/providersAPI'
import { getErrorMessage } from '@/utils/errorHandlers'
import { AsyncSelectForm } from '@/components/forms/AsyncSelectForm'
import Select from '@/components/forms/Select'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import { TextAreaForm, ToggleForm } from '@/components/new'
import { InputForm } from '@/components/new/form/InputForm'
import { SelectForm } from '@/components/new/form/SelectForm'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { Label } from '@/components/ui/label'
import useContainerWizzardSteps, {
  Selector,
} from '@/state/useContainerWizzardSteps'
import { formatDateForTableSeparatedDateAndTime } from '@/utils/helpers'

import { containerStatusOptions } from '../../../filters/constants/statusOptions'
import { useContainerDetails } from '../../../hooks/useContainerDetails'
import { IContainer } from '../../../interfaces/interfaces'

export default function DetailsContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const { control, setValue, getValues, watch } = useFormWizzard

  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const queryClient = useQueryClient()

  // Use shared container details hook
  const { containerDetails, isLoading } = useContainerDetails(editContainer?.id)

  // Queries for async data
  const { data: categories } = useQuery({
    queryKey: ['container_categories'],
    queryFn: () => getContainerCategories(),
    staleTime: 5 * 60 * 1000,
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
    staleTime: 5 * 60 * 1000,
  })

  // Async fetchers for AsyncSelectForm
  const fetchProviders = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    const response = await getProviders({ page: 1, per_page: 10, search })
    if (!Array.isArray(response)) return []
    return response.map(({ id, name }) => ({
      id: String(id),
      label: name,
    }))
  }

  const fetchManagers = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    const response = await getManagers({ page: 1, per_page: 10, search })
    if (!Array.isArray(response)) return []
    return response.map(({ id, name }) => ({
      id: String(id),
      label: name,
    }))
  }

  const fetchCategories = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    const response = await getContainerCategories()
    if (!Array.isArray(response)) return []
    return response
      .filter((category) =>
        category.name.toLowerCase().includes(search.toLowerCase())
      )
      .map(({ id, name }) => ({
        id: String(id),
        label: name,
      }))
  }

  const fetchCountries = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    // Usar datos de useQuery si están disponibles
    let countryList = countries
    if (!countryList) {
      countryList = await getCountries()
    }
    if (!Array.isArray(countryList)) return []
    return countryList
      .filter((country) =>
        country.name.toLowerCase().includes(search.toLowerCase())
      )
      .map(({ id, name }) => ({
        id: String(id),
        label: name,
      }))
  }

  // Set form values when container details are loaded
  useEffect(() => {
    if (containerDetails && isEditing) {
      setValue('status', containerDetails.status)

      // Para country, extraer solo el ID si existe el objeto
      if (containerDetails.countryFrom) {
        setValue('countryFrom', {
          id: containerDetails.countryFrom.id,
          label: containerDetails.countryFrom.name,
        })
      }

      // Para categories, extraer solo los IDs si existe el array
      if (
        containerDetails.categories &&
        Array.isArray(containerDetails.categories)
      ) {
        const categoryOptions = containerDetails.categories.map((cat: any) => ({
          id: cat.id,
          label: cat.name,
        }))
        setValue('categories', categoryOptions)
      }

      setValue('isReturned', containerDetails.isReturned)

      // Para ownerCompany, extraer solo el ID si existe el objeto
      if (containerDetails.ownerCompany) {
        setValue('ownerCompany', {
          id: containerDetails.ownerCompany.id,
          label: containerDetails.ownerCompany.name,
        })
      }

      // Para managerCompany, extraer solo el ID si existe el objeto
      if (containerDetails.managerCompany) {
        setValue('managerCompany', {
          id: containerDetails.managerCompany.id,
          label: containerDetails.managerCompany.name,
        })
      }

      setValue('observations', containerDetails.observations)
    }
  }, [containerDetails, isEditing, setValue])

  // Mutation for updating container details
  const updateContainerDetails = useMutation({
    mutationFn: async (detailsData: {
      status?: string
      countryFromId?: number
      categories?: any[]
      isReturned?: boolean
      ownerCompanyId?: number
      managerCompanyId?: number
      observations?: string
    }) => {
      if (!editContainer?.id) throw new Error('Container ID is required')

      // Prepare the body according to the API specification
      const body: any = {}

      // Include only editable fields
      if (detailsData.status) {
        body.status = detailsData.status
      }
      if (detailsData.observations !== undefined) {
        body.observations = detailsData.observations
      }
      if (detailsData.categories) {
        body.categories = detailsData.categories
      }
      if (detailsData.ownerCompanyId !== undefined) {
        body.ownerCompanyId = detailsData.ownerCompanyId
      }
      if (detailsData.managerCompanyId !== undefined) {
        body.managerCompanyId = detailsData.managerCompanyId
      } else {
        body.managerCompanyId = null
      }
      if (detailsData.countryFromId !== undefined) {
        body.countryFromId = detailsData.countryFromId
      }
      if (detailsData.isReturned !== undefined) {
        body.isReturned = detailsData.isReturned
      }

      const response = await APIServicesV2.patch(
        `/containers/${editContainer.id}/details`,
        body
      )
      return response.data
    },
    onSuccess: () => {
      toast.success('Información de detalles actualizada correctamente')
      queryClient.invalidateQueries({
        queryKey: ['container_details', editContainer?.id],
      })
      queryClient.refetchQueries({ queryKey: ['containers'] })
      // No cerrar modal automáticamente en modo edición
    },
    onError: (error: any) => {
      const errorMessage = getErrorMessage(error)
      toast.error(
        errorMessage || 'Error al actualizar la información de detalles'
      )
    },
  })

  // Function to handle saving details data
  const handleSaveDetails = async () => {
    const formValues = getValues()

    const detailsData: {
      status?: string
      countryFromId?: number
      categories?: any[]
      isReturned?: boolean
      ownerCompanyId?: number
      managerCompanyId?: number
      observations?: string
    } = {}

    // Only include fields that have values
    if (formValues.status) {
      detailsData.status = formValues.status
    }
    if (formValues.countryFrom?.id) {
      detailsData.countryFromId = Number(formValues.countryFrom.id)
    }
    if (formValues.categories) {
      // Transform categories from objects to array of IDs
      detailsData.categories = Array.isArray(formValues.categories)
        ? formValues.categories.map((category) =>
            typeof category === 'object' && category.id
              ? Number(category.id)
              : Number(category)
          )
        : []
    }
    if (formValues.isReturned !== undefined) {
      detailsData.isReturned = formValues.isReturned
    }
    detailsData.ownerCompanyId = formValues.ownerCompany
      ? typeof formValues.ownerCompany === 'object' &&
        formValues.ownerCompany !== null
        ? Number(formValues.ownerCompany.id)
        : Number(formValues.ownerCompany)
      : null
    detailsData.managerCompanyId = formValues.managerCompany
      ? typeof formValues.managerCompany === 'object' &&
        formValues.managerCompany !== null
        ? Number(formValues.managerCompany.id)
        : Number(formValues.managerCompany)
      : null
    if (formValues.observations) {
      detailsData.observations = formValues.observations
    }

    // DEBUG: formValues.managerCompany: ${JSON.stringify(formValues.managerCompany)}
    // DEBUG: detailsData.managerCompanyId: ${detailsData.managerCompanyId}

    await updateContainerDetails.mutateAsync(detailsData)
  }

  // Expose the save function to the parent component
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveDetails = handleSaveDetails
      // @ts-ignore - Expose loading state
      window.isSavingDetails = updateContainerDetails.isPending
    }

    return () => {
      // @ts-ignore
      delete window.handleSaveDetails
      // @ts-ignore
      delete window.isSavingDetails
    }
  }, [isEditing, handleSaveDetails, updateContainerDetails.isPending])

  // Watch form changes and update data - using a single useEffect to avoid infinite loops
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        updateData({ ...data, [name]: value[name] })
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, updateData])

  return isLoading && isEditing ? (
    <SpinnerLoading />
  ) : (
    <div className={'h-[60dvh] overflow-y-auto'}>
      <div
        className={'space-y-5'}
        style={isEditing ? { marginTop: '-.5rem' } : {}}
      >
        {/* Estado */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <SelectForm
              control={control}
              data={containerStatusOptions
                .filter((item) => item.label !== 'Todos')
                .map((item) => ({ id: item.value, title: item.label }))}
              defaultValue={containerDetails?.status}
              label="Estado"
              name="status"
            />
          </div>
        </div>

        {/* Creado el */}
        {isEditing && containerDetails?.createdAt && (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium text-gray-700">
                  Creado el
                </Label>
                <span className="text-sm text-gray-900">
                  {(() => {
                    const { date, time } =
                      formatDateForTableSeparatedDateAndTime(
                        containerDetails.createdAt
                      )
                    return `${date} ${time}`
                  })()}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Número de operación */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-gray-700">
                Número de operación
              </Label>
              <span className="text-sm text-gray-900">
                {containerDetails?.operationNumber || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* País de origen */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <AsyncSelectForm
              control={control}
              defaultValue={containerDetails?.countryFrom}
              fetcher={fetchCountries}
              label="País de origen"
              name="countryFrom"
              notFoundMessage="No se encontraron países."
              placeholder="Buscar país..."
            />
          </div>
        </div>

        {/* Categorías */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <AsyncSelectForm
              control={control}
              defaultValue={containerDetails?.categories}
              fetcher={fetchCategories}
              label="Categorías"
              multiple={true}
              name="categories"
              disableDefaultValueCaching
              notFoundMessage="No se encontraron categorías."
              placeholder="Buscar categorías..."
            />
          </div>
        </div>

        {/* Retornado */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <ToggleForm
              control={control}
              defaultValue={containerDetails?.isReturned}
              label="Retornado"
              name="isReturned"
            />
          </div>
        </div>

        {/* Compañía proveedora */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <AsyncSelectForm
              control={control}
              fetcher={fetchProviders}
              label="Compañía proveedora"
              name="ownerCompany"
              notFoundMessage="No se encontraron proveedores."
              placeholder="Buscar proveedor..."
            />
          </div>
        </div>

        {/* Operador logístico */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <AsyncSelectForm
              control={control}
              fetcher={fetchManagers}
              label="Operador logístico"
              name="managerCompany"
              notFoundMessage="No se encontraron operadores."
              placeholder="Buscar operador..."
            />
          </div>
        </div>

        {/* Observaciones */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <TextAreaForm
              control={control}
              defaultValue={containerDetails?.observations}
              label="Observaciones"
              name="observations"
              placeholder="Observaciones"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
